/**
 * System-wide enums. Exported as readonly union types so they can be reused on both
 * the API (TypeORM column types, GraphQL enums) and the web app (Zod schemas, UI labels).
 *
 * UI labels live in i18n files keyed by `enum::<EnumName>::<Value>` — see `i18n-keys.ts`.
 */

export const SaleOrderStatus = {
  CreatedByCashier: 'created_by_cashier',
  SentToDoctorPendingTake: 'sent_to_doctor_pending_take',
  TakenByDoctor: 'taken_by_doctor',
  InMedicalAttention: 'in_medical_attention',
  ReturnedToCashierForCorrection: 'returned_to_cashier_for_correction',
  SentToCashierForCharge: 'sent_to_cashier_for_charge',
  Charged: 'charged',
  // Backward-compatible legacy statuses kept for historical rows.
  Draft: 'draft',
  SentToCashier: 'sent_to_cashier',
  ReturnedToDoctor: 'returned_to_doctor',
  PendingPayment: 'pending_payment',
  Paid: 'paid',
  PartiallyPaid: 'partially_paid',
  OpenAccount: 'open_account',
  Cancelled: 'cancelled',
  Voided: 'voided',
  Refunded: 'refunded',
} as const;
export type SaleOrderStatus = (typeof SaleOrderStatus)[keyof typeof SaleOrderStatus];

export const PaymentMethod = {
  Cash: 'cash',
  CreditCard: 'credit_card',
  DebitCard: 'debit_card',
} as const;
export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod];

export const OtpAction = {
  Discount: 'discount',
  SaleCancel: 'sale_cancel',
  CashOut: 'cash_out',
  CashIn: 'cash_in',
  AdminOverride: 'admin_override',
  PriceOverride: 'price_override',
} as const;
export type OtpAction = (typeof OtpAction)[keyof typeof OtpAction];

export const OtpStatus = {
  Active: 'active',
  Burned: 'burned',
  Revoked: 'revoked',
  Expired: 'expired',
} as const;
export type OtpStatus = (typeof OtpStatus)[keyof typeof OtpStatus];

export const UserRole = {
  Administrator: 'administrator',
  Cashier: 'cashier',
  Veterinarian: 'veterinarian',
  Assistant: 'assistant',
  InfoPanel: 'info_panel',
  InfoPanel2: 'info_panel_2',
} as const;
export type UserRole = (typeof UserRole)[keyof typeof UserRole];

export const WorkstationKind = {
  Cashier: 'cashier',
  ConsultingRoom: 'consulting_room',
  Admin: 'admin',
  Mixed: 'mixed',
} as const;
export type WorkstationKind = (typeof WorkstationKind)[keyof typeof WorkstationKind];

export const CashShiftStatus = {
  Open: 'open',
  Closed: 'closed',
  Locked: 'locked',
} as const;
export type CashShiftStatus = (typeof CashShiftStatus)[keyof typeof CashShiftStatus];

export const CashMovementKind = {
  CashIn: 'cash_in',
  CashOut: 'cash_out',
  SurpriseCount: 'surprise_count',
} as const;
export type CashMovementKind = (typeof CashMovementKind)[keyof typeof CashMovementKind];

export const InventoryMovementKind = {
  Purchase: 'purchase',
  Adjustment: 'adjustment',
  Sale: 'sale',
  ServiceConsumption: 'service_consumption',
  KitConsumption: 'kit_consumption',
  RestorationOnCancel: 'restoration_on_cancel',
  SurpriseCount: 'surprise_count',
} as const;
export type InventoryMovementKind =
  (typeof InventoryMovementKind)[keyof typeof InventoryMovementKind];

export const OpenAccountStatus = {
  Open: 'open',
  Settled: 'settled',
  Closed: 'closed',
} as const;
export type OpenAccountStatus = (typeof OpenAccountStatus)[keyof typeof OpenAccountStatus];

export const InvoiceStatus = {
  Pending: 'pending',
  Stamping: 'stamping',
  Stamped: 'stamped',
  CancelRequested: 'cancel_requested',
  Cancelled: 'cancelled',
  CancelFailed: 'cancel_failed',
  Failed: 'failed',
} as const;
export type InvoiceStatus = (typeof InvoiceStatus)[keyof typeof InvoiceStatus];

/** SAT cancellation motive codes. */
export const CfdiCancelReason = {
  /** 01 — Comprobante emitido con errores con relación. */
  WithErrorsRelated: '01',
  /** 02 — Comprobante emitido con errores sin relación. */
  WithErrorsUnrelated: '02',
  /** 03 — No se llevó a cabo la operación. */
  NotPerformed: '03',
  /** 04 — Operación nominativa relacionada en la factura global. */
  RelatedToGlobalInvoice: '04',
} as const;
export type CfdiCancelReason = (typeof CfdiCancelReason)[keyof typeof CfdiCancelReason];

/**
 * Clinical record (Phase 1) enums. Mirrors the DB ENUM columns on the
 * `clinical_*` tables and is re-declared as GraphQL `registerEnumType` in the API
 * DTO layer. UI labels live in the web `clinical` i18n namespace.
 */

/**
 * Clinical visit lifecycle. `pending_signature` is RESERVED for a future
 * signature step — no Phase 1 transition targets it (kept so the persisted ENUM
 * is forward-compatible). See clinical-record-spec/01 §3.
 */
export const ClinicalVisitStatus = {
  Draft: 'draft',
  InProgress: 'in_progress',
  PendingCompletion: 'pending_completion',
  PendingSignature: 'pending_signature',
  Closed: 'closed',
  Addended: 'addended',
  Cancelled: 'cancelled',
  Voided: 'voided',
} as const;
export type ClinicalVisitStatus =
  (typeof ClinicalVisitStatus)[keyof typeof ClinicalVisitStatus];

export const ClinicalVisitKind = {
  Consultation: 'consultation',
  FollowUp: 'follow_up',
  Emergency: 'emergency',
  Vaccination: 'vaccination',
  Procedure: 'procedure',
  Grooming: 'grooming',
  Other: 'other',
} as const;
export type ClinicalVisitKind =
  (typeof ClinicalVisitKind)[keyof typeof ClinicalVisitKind];

export const DiagnosisKind = {
  Presumptive: 'presumptive',
  Differential: 'differential',
  Confirmed: 'confirmed',
  RuleOut: 'rule_out',
} as const;
export type DiagnosisKind = (typeof DiagnosisKind)[keyof typeof DiagnosisKind];

export const DiagnosisPrognosis = {
  Excellent: 'excellent',
  Good: 'good',
  Guarded: 'guarded',
  Poor: 'poor',
  Grave: 'grave',
  Unknown: 'unknown',
} as const;
export type DiagnosisPrognosis =
  (typeof DiagnosisPrognosis)[keyof typeof DiagnosisPrognosis];

/** Shared by `vaccination_status` and `deworming_status` on `clinical_anamnesis`. */
export const PreventiveCareStatus = {
  UpToDate: 'up_to_date',
  Overdue: 'overdue',
  Unknown: 'unknown',
  NotApplicable: 'not_applicable',
} as const;
export type PreventiveCareStatus =
  (typeof PreventiveCareStatus)[keyof typeof PreventiveCareStatus];

export const MucousMembranes = {
  Normal: 'normal',
  Pale: 'pale',
  Icteric: 'icteric',
  Cyanotic: 'cyanotic',
  Hyperemic: 'hyperemic',
  Dry: 'dry',
  Tacky: 'tacky',
} as const;
export type MucousMembranes =
  (typeof MucousMembranes)[keyof typeof MucousMembranes];

export const HydrationStatus = {
  Normal: 'normal',
  MildDehydration: 'mild_dehydration',
  ModerateDehydration: 'moderate_dehydration',
  SevereDehydration: 'severe_dehydration',
} as const;
export type HydrationStatus =
  (typeof HydrationStatus)[keyof typeof HydrationStatus];

export const ConsciousnessLevel = {
  Alert: 'alert',
  Depressed: 'depressed',
  Obtunded: 'obtunded',
  Stuporous: 'stuporous',
  Comatose: 'comatose',
} as const;
export type ConsciousnessLevel =
  (typeof ConsciousnessLevel)[keyof typeof ConsciousnessLevel];

/** Hard limits set by business rules (rule §11). */
export const Limits = {
  /** Cash-out cap: 500 MXN per movement. */
  CASH_OUT_MAX_CENTS: 50_000n,
} as const;

/** Calculation Engine contract version (rule §13). */
export const CALCULATION_VERSION = '1.0.0';
